// Browser-only drafts for the new-work-order form. Storage can be unavailable
// (private windows, blocked site data), so every access is guarded.
import type { WorkOrderInput } from './types';

const PREFIX = 'uncle-oms:work-order-draft:';

export interface WorkOrderDraft {
  input: WorkOrderInput;
  mode: 'existing' | 'new';
  savedAt: string;
}

function key(userId: string): string {
  return `${PREFIX}${userId}`;
}

export function loadDraft(userId: string): WorkOrderDraft | null {
  try {
    const raw = localStorage.getItem(key(userId));
    if (!raw) return null;
    const draft = JSON.parse(raw) as WorkOrderDraft;
    return draft?.input && draft.savedAt ? draft : null;
  } catch {
    return null;
  }
}

export function saveDraft(userId: string, draft: WorkOrderDraft): boolean {
  try {
    localStorage.setItem(key(userId), JSON.stringify(draft));
    return true;
  } catch {
    return false;
  }
}

export function clearDraft(userId: string): void {
  try {
    localStorage.removeItem(key(userId));
  } catch {
    // Nothing to clear when storage is unavailable.
  }
}

/** A draft is worth keeping once anything beyond the defaults has been typed. */
export function isMeaningfulDraft(input: WorkOrderInput): boolean {
  return Boolean(
    input.clientId
    || input.newCustomer?.name.trim()
    || input.details.trim()
    || input.items.some((item) => item.description.trim() || item.departmentIds.length)
    || input.installation.required
    || input.repair.required
    || input.deliveryAddress.trim()
    || input.quote,
  );
}
